import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { useDispatch } from 'react-redux';
import { Link as RouterLink } from 'react-router-dom';
import {
    Autocomplete,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Divider,
    FormControlLabel,
    FormHelperText,
    IconButton,
    InputAdornment,
    Link,
    MenuItem,
    Paper,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Tooltip,
    Typography,
    useMediaQuery,
} from '@mui/material';
import {
    Add as AddIcon,
    Search as SearchIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    RestoreFromTrash as RestoreIcon,
    TravelExplore as UsageIcon,
    LanguageOutlined as ContextIcon,
    RemoveCircleOutline as RemoveIcon,
    Visibility as VisibilityIcon,
    VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import { AdminPageContainer, FormField, Row, HalfWidthField } from '../components/admin/sharedStyles';
import MarkdownEditor from '../components/MarkdownEditor';
import PageHeader from '../components/PageHeader';
import ConfirmDialog from '../components/admin/ConfirmDialog';
import { getTags, createTag, updateTag, deleteTag, restoreTag, getTagUsage, getTagDetail, updateTagContext } from '../redux/ducks/tag';
import { getClients } from '../redux/ducks/client';
import { showAlert } from '../redux/slices/alerts';
import { formatDateTime } from '../utils/dateUtils';
import { accountPath, projectPath, splitUrlCredentials, urlHasLogin } from '../utils/navigateUtils';
import { buildLexicalFromPlainText, contentToPlainText, isLexicalJson } from '../utils/lexicalContent';

const PAGE_SIZE = 25;
const SEARCH_DELAY_MS = 300;
const ALL_CLIENTS = { _id: '', name: 'All clients' };
const GENERAL = { _id: 'none', name: 'General (no client)' };

const clientOptionLabel = (option) => (option.active === false ? `${option.name} (inactive)` : option.name || '');

const FilterRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(3),
}));

const SearchField = styled(TextField)({
    flex: '1 1 280px',
});

const ClientFilter = styled(Autocomplete)(({ theme }) => ({
    flex: '0 1 280px',
    minWidth: 220,
    [theme.breakpoints.down('sm')]: {
        flex: '1 1 100%',
    },
}));

const CenteredState = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'center',
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
}));

const TagRow = styled(TableRow, { shouldForwardProp: (prop) => prop !== 'muted' })(({ muted }) => ({
    opacity: muted ? 0.55 : 1,
}));

const NoWrapCell = styled(TableCell)({
    whiteSpace: 'nowrap',
});

const WideOnlyCell = styled(TableCell)(({ theme }) => ({
    whiteSpace: 'nowrap',
    [theme.breakpoints.down('md')]: {
        display: 'none',
    },
}));

const ScrollContainer = styled(TableContainer)(({ theme }) => ({
    overflowX: 'auto',
    '&::-webkit-scrollbar': {
        height: 8,
    },
    '&::-webkit-scrollbar-thumb': {
        backgroundColor: theme.palette.divider,
        borderRadius: 4,
    },
}));

const TagTable = styled(Table)(({ theme }) => ({
    [theme.breakpoints.down('sm')]: {
        '& .MuiTableCell-root': {
            padding: theme.spacing(0.75, 1),
        },
        '& .MuiTableCell-root:last-of-type': {
            paddingRight: theme.spacing(0.5),
        },
    },
}));

const CodeCell = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
}));

const CodeText = styled(Typography)({
    fontWeight: 600,
    overflowWrap: 'anywhere',
});

const NarrowLabel = styled(Typography)(({ theme }) => ({
    display: 'none',
    color: theme.palette.text.secondary,
    [theme.breakpoints.down('md')]: {
        display: 'block',
    },
}));

const FirstField = styled(FormField)(({ theme }) => ({
    marginTop: theme.spacing(1),
}));

const UsageBody = styled(Box)(({ theme }) => ({
    marginTop: theme.spacing(2),
}));

const UsageSection = styled(Box)(({ theme }) => ({
    marginBottom: theme.spacing(2),
}));

const UsageList = styled('ul')(({ theme }) => ({
    margin: 0,
    paddingLeft: theme.spacing(2.5),
}));

const TagFormDialog = ({ open, tag, clients, onClose, onSaved }) => {
    const dispatch = useDispatch();
    const isEdit = Boolean(tag);
    const [code, setCode] = useState('');
    const [label, setLabel] = useState('');
    const [client, setClient] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open) return;
        setCode(tag?.code || '');
        setLabel(tag?.label || '');
        setClient(null);
        setSaving(false);
    }, [open, tag]);

    const canSave = code.trim() !== '' && (isEdit || client) && !saving;

    const handleSave = () => {
        setSaving(true);
        const done = (data) => {
            setSaving(false);
            if (!data?.tag) return;
            dispatch(showAlert({ severity: 'success', message: data.message || 'Tag saved.' }));
            onSaved();
        };
        if (isEdit) {
            dispatch(updateTag(tag._id, { code: code.trim(), label: label.trim() || null }, done));
        } else {
            dispatch(createTag({ code: code.trim(), label: label.trim() || undefined, client: client._id }, done));
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>{isEdit ? 'Edit tag' : 'Create tag'}</DialogTitle>
            <DialogContent>
                <DialogContentText>{isEdit ? 'Change the tag’s code or label.' : 'Creates a tag that can then be added to work.'}</DialogContentText>
                <FirstField>
                    <TextField fullWidth autoFocus required label="Code" placeholder="e.g. SSR1002M" value={code} onChange={(e) => setCode(e.target.value)} />
                </FirstField>
                <FormField>
                    <TextField fullWidth label="Label" placeholder="Optional display name" value={label} onChange={(e) => setLabel(e.target.value)} />
                </FormField>
                <FormField>
                    {isEdit ? (
                        <TextField fullWidth disabled label="Client" value={tag.client?.name || GENERAL.name} />
                    ) : (
                        <Autocomplete
                            options={[GENERAL, ...clients]}
                            value={client}
                            onChange={(event, next) => setClient(next)}
                            getOptionLabel={clientOptionLabel}
                            isOptionEqualToValue={(option, value) => option._id === value._id}
                            renderInput={(params) => <TextField {...params} required label="Client" placeholder="Choose a client or General" />}
                        />
                    )}
                </FormField>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button variant="contained" onClick={handleSave} disabled={!canSave}>
                    {saving ? 'Saving…' : isEdit ? 'Save tag' : 'Create tag'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

const UsageGroup = ({ title, items, total, renderItem }) => {
    if (!items?.length) return null;
    return (
        <UsageSection>
            <Typography variant="subtitle2">
                {title} ({total ?? items.length})
            </Typography>
            <UsageList>
                {items.map((item) => (
                    <li key={item._id}>
                        <Typography variant="body2">{renderItem(item)}</Typography>
                    </li>
                ))}
            </UsageList>
            {total > items.length && (
                <Typography variant="caption" color="text.secondary">
                    Showing the latest {items.length} of {total}.
                </Typography>
            )}
        </UsageSection>
    );
};

const linkOrText = (href, text) =>
    href ? (
        <Link component={RouterLink} to={href} underline="hover">
            {text}
        </Link>
    ) : (
        text
    );

const withAccount = (item) => (item.account?.name ? `${item.name} · ${item.account.name}` : item.name);

const TagUsageDialog = ({ tag, onClose }) => {
    const dispatch = useDispatch();
    const [usage, setUsage] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!tag) return;
        setUsage(null);
        setLoading(true);
        dispatch(
            getTagUsage(tag._id, (data) => {
                setUsage(data);
                setLoading(false);
            }),
        );
    }, [dispatch, tag]);

    const counts = usage?.counts || {};
    const isEmpty = usage && ['clients', 'accounts', 'groups', 'projects', 'tasks'].every((key) => !usage[key]?.length);

    return (
        <Dialog open={Boolean(tag)} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Where “{tag?.code}” is used</DialogTitle>
            <DialogContent>
                <DialogContentText>Everything this tag is currently assigned to.</DialogContentText>
                {loading ? (
                    <CenteredState>
                        <CircularProgress size={28} />
                    </CenteredState>
                ) : !usage ? (
                    <Typography color="error">Usage couldn’t be loaded. Try again.</Typography>
                ) : isEmpty ? (
                    <Typography color="text.secondary">This tag isn’t assigned to anything yet.</Typography>
                ) : (
                    <UsageBody>
                        <UsageGroup title="Clients" items={usage.clients} total={counts.clients} renderItem={(item) => linkOrText(`/client/${item._id}`, item.name)} />
                        <UsageGroup title="Accounts" items={usage.accounts} total={counts.accounts} renderItem={(item) => linkOrText(accountPath(item), item.name)} />
                        <UsageGroup title="Groups" items={usage.groups} total={counts.groups} renderItem={(item) => linkOrText(accountPath(item.account), withAccount(item))} />
                        <UsageGroup title="Projects" items={usage.projects} total={counts.projects} renderItem={(item) => linkOrText(projectPath(item.account, item), withAccount(item))} />
                        <UsageGroup title="Tasks" items={usage.tasks} total={counts.tasks} renderItem={(item) => linkOrText(projectPath(item.project?.account, item.project), item.name)} />
                    </UsageBody>
                )}
            </DialogContent>
            <DialogActions>
                <Button variant="contained" onClick={onClose}>
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};

const SITE_TYPES = [
    { value: 'wordpress', label: 'WordPress' },
    { value: 'shopify', label: 'Shopify' },
    { value: 'static', label: 'Static' },
    { value: 'other', label: 'Other' },
];

const ENVIRONMENT_NAMES = ['Live', 'Test', 'Dev', 'Autopilot'];

const QA_BOARD_TYPES = ['BugHerd'];

const COLLECTORS = [
    { key: 'uptime', label: 'Uptime' },
    { key: 'brokenLinks', label: 'Broken links' },
    { key: 'malware', label: 'Malware' },
    { key: 'accessibility', label: 'Accessibility' },
    { key: 'pagespeed', label: 'PageSpeed' },
    { key: 'backups', label: 'Backups' },
    { key: 'imageOptimization', label: 'Image optimisation' },
    { key: 'analytics', label: 'Analytics' },
];

const RICH_FIELDS = ['testInstructions', 'notes', 'agentInstructions'];

const missingEnvironmentName = (env) =>
    !trimmed(env.name) && Boolean(trimmed(env.url) || trimmed(env.vaultRef) || trimmed(env.branch) || trimmed(env.username) || trimmed(env.password));

const trimmed = (value) => (typeof value === 'string' ? value.trim() : '');

const CREDENTIAL_LINK = /[a-z][a-z0-9+.-]*:\/\/[^/\s@:]+:[^/\s@]+@/i;
const CREDENTIAL_LINK_WARNING = 'This includes a link with a username and password.';

const secretWarning = (value) => {
    const text = trimmed(value);
    if (!text) return '';
    if (/-----BEGIN [A-Z ]*PRIVATE KEY-----/.test(text)) return 'This looks like a private key. Store it in the vault and link to it here.';
    if (/(pass(word)?|pwd|secret|token|api[_-]?key|access[_-]?key)\s*[=:]/i.test(text)) return 'This looks like it contains a password or token. Use a link or vault reference instead.';
    const looksLikeLink = /^[a-z][a-z0-9+.-]*:/i.test(text) || /^[\w-]+(\.[\w-]+)+(:\d+)?(\/|$)/.test(text);
    if (!/\s/.test(text) && !looksLikeLink && !/^[0-9a-f-]+$/i.test(text)) {
        const classes = [/[a-z]/, /[A-Z]/, /[0-9]/, /[^A-Za-z0-9]/].filter((pattern) => pattern.test(text)).length;
        if ((text.length >= 16 && classes >= 3) || (text.length >= 32 && classes >= 2)) return 'This looks like a password or token. Use a link or vault reference instead.';
    }
    return CREDENTIAL_LINK.test(text) ? CREDENTIAL_LINK_WARNING : '';
};

const richSecretWarning = (content) => {
    const text = content ? contentToPlainText(content) : '';
    if (!text) return '';
    if (/-----BEGIN [A-Z ]*PRIVATE KEY-----/.test(text)) return 'This includes what looks like a private key. Store it in the vault and link to it here.';
    if (/(pass(word)?|pwd|secret|token|api[_-]?key|access[_-]?key)\s*[=:]\s*\S/i.test(text)) return 'This looks like it contains a password or token. Use a link or vault reference instead.';
    const tokenLike = text.split(/\s+/).some((word) => {
        const token = word.replace(/^[("'`]+|[)"'`.,;]+$/g, '');
        if (!/[0-9]/.test(token) || /^[a-z][a-z0-9+.-]*:/i.test(token) || /^[\w-]+(\.[\w-]+)+(:\d+)?(\/|$)/.test(token) || /^[0-9a-f-]+$/i.test(token)) return false;
        const classes = [/[a-z]/, /[A-Z]/, /[0-9]/, /[^A-Za-z0-9]/].filter((pattern) => pattern.test(token)).length;
        return (token.length >= 16 && classes >= 3) || (token.length >= 32 && classes >= 2);
    });
    if (tokenLike) return 'This includes what looks like a password or token. Use a link or vault reference instead.';
    return CREDENTIAL_LINK.test(text) ? CREDENTIAL_LINK_WARNING : '';
};

const URL_SCHEME = /^([a-z][a-z0-9+.-]*):(?!\d)/i;

const urlWarning = (value) => {
    const text = trimmed(value);
    if (!text) return '';
    if (/\s/.test(text)) return 'A web address can’t contain spaces.';
    const scheme = text.match(URL_SCHEME);
    if (scheme && /^[^/\s@:]+:[^/\s@]+@/.test(text)) return 'Start a link that includes a username and password with https://.';
    return scheme && !/^https?:\/\/\S+$/i.test(text) ? 'Use a web address starting with http:// or https://, or a plain domain like example.com.' : '';
};

const environmentUrlWarning = (value) => {
    const { url } = splitUrlCredentials(trimmed(value));
    return urlHasLogin(url) ? 'This URL includes a login. Put it in the Username and Password fields instead.' : urlWarning(url);
};

const MAX_ENVIRONMENTS = 20;

let environmentKey = 0;
const withKey = (env) => ({ ...env, key: ++environmentKey });

const environmentForm = (env) => {
    const parsed = splitUrlCredentials(env?.url || '');
    const explicit = Boolean(env?.username || env?.password);
    return withKey({
        name: env?.name || '',
        url: parsed.url,
        username: explicit ? env?.username || '' : parsed.username,
        password: explicit ? env?.password || '' : parsed.password,
        vaultRef: env?.vaultRef || '',
        branch: env?.branch || '',
    });
};

const formFromContext = (context) => ({
    site: context?.site || '',
    type: context?.type || '',
    hostProvider: context?.host?.provider || '',
    hostDashboardUrl: context?.host?.dashboardUrl || '',
    hostSiteId: context?.host?.siteId || '',
    repositoryUrl: context?.repository?.url || '',
    environments: (Array.isArray(context?.environments) ? context.environments : []).map(environmentForm),
    designsUrl: context?.designsUrl || '',
    qaBoardType: context?.qaBoard?.type || '',
    qaBoardUrl: context?.qaBoard?.url || '',
    credentialRef: context?.credentialRef || '',
    adminRef: context?.adminRef || '',
    sshRef: context?.sshRef || '',
    collectors: Object.fromEntries(
        Object.entries(context?.collectors && typeof context.collectors === 'object' ? context.collectors : {})
            .filter(([, value]) => value)
            .map(([key, value]) => [key, typeof value === 'object' ? value : {}]),
    ),
});

const withoutEmpty = (entries) => {
    const kept = entries.filter(([, value]) => value !== undefined && value !== '');
    return kept.length ? Object.fromEntries(kept) : undefined;
};

const toLexical = (value) => {
    if (value && typeof value === 'object') return value.root?.children ? JSON.stringify(value) : undefined;
    if (typeof value !== 'string') return undefined;
    return isLexicalJson(value) ? value : buildLexicalFromPlainText(value);
};

const richValue = (value) => (value && contentToPlainText(value) !== '' ? toLexical(value) : undefined);

const buildContext = (form, rich) => {
    const collectors = Object.fromEntries(
        Object.entries(form.collectors).map(([key, value]) => {
            if (key !== 'analytics') return [key, value || {}];
            const { expectedTag, ...rest } = value || {};
            return [key, trimmed(expectedTag) ? { ...rest, expectedTag: trimmed(expectedTag) } : rest];
        }),
    );
    const environments = form.environments
        .map((env) =>
            withoutEmpty([
                ['name', trimmed(env.name)],
                ['url', trimmed(env.url)],
                ['username', trimmed(env.username)],
                ['password', trimmed(env.password)],
                ['vaultRef', trimmed(env.vaultRef)],
                ['branch', trimmed(env.branch)],
            ]),
        )
        .filter(Boolean);
    return (
        withoutEmpty([
            ['site', trimmed(form.site)],
            ['host', withoutEmpty([['provider', trimmed(form.hostProvider)], ['dashboardUrl', trimmed(form.hostDashboardUrl)], ['siteId', trimmed(form.hostSiteId)]])],
            ['repository', withoutEmpty([['url', trimmed(form.repositoryUrl)]])],
            ['environments', environments.length ? environments : undefined],
            ['designsUrl', trimmed(form.designsUrl)],
            ['qaBoard', withoutEmpty([['type', trimmed(form.qaBoardType)], ['url', trimmed(form.qaBoardUrl)]])],
            ['testInstructions', richValue(rich.testInstructions)],
            ['notes', richValue(rich.notes)],
            ['type', form.type],
            ['credentialRef', trimmed(form.credentialRef)],
            ['adminRef', trimmed(form.adminRef)],
            ['sshRef', trimmed(form.sshRef)],
            ['collectors', Object.keys(collectors).length ? collectors : undefined],
            ['agentInstructions', richValue(rich.agentInstructions)],
        ]) || {}
    );
};

const SectionTitle = styled(Typography)(({ theme }) => ({
    fontWeight: theme.typography.fontWeightMedium,
    marginTop: theme.spacing(1),
}));

const SectionHelper = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(2),
}));

const ServiceSection = styled(Box)(({ theme }) => ({
    marginTop: theme.spacing(3),
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius * 2,
    border: `1px dashed ${theme.palette.divider}`,
    backgroundColor: theme.palette.action.hover,
    [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(1.5),
    },
}));

const FieldLabel = styled(Typography)(({ theme }) => ({
    fontSize: theme.typography.size.sm,
    fontWeight: theme.typography.fontWeightMedium,
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(0.75),
}));

const EnvironmentRow = styled(Box)(({ theme }) => ({
    display: 'grid',
    gridTemplateColumns: 'minmax(120px, 0.8fr) minmax(0, 1.4fr) minmax(0, 1.4fr) minmax(90px, 0.8fr) auto',
    gridTemplateAreas: '"name url url branch remove" "username password vault vault remove"',
    gap: theme.spacing(1),
    alignItems: 'start',
    marginBottom: theme.spacing(1.5),
    paddingBottom: theme.spacing(1.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
    [theme.breakpoints.down('md')]: {
        gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr) auto',
        gridTemplateAreas: '"name url remove" "username password remove" "vault branch remove"',
    },
}));

const EnvironmentCell = styled(Box, { shouldForwardProp: (prop) => prop !== '$area' })(({ $area }) => ({
    gridArea: $area,
    minWidth: 0,
}));

const TooltipAnchor = styled.span({
    display: 'inline-flex',
    alignSelf: 'flex-start',
});

const RemoveEnvButton = styled(IconButton)(({ theme }) => ({
    gridArea: 'remove',
    marginTop: theme.spacing(1),
}));

const CollectorGrid = styled(Box)(({ theme }) => ({
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))',
    columnGap: theme.spacing(2),
}));

const TightDivider = styled(Divider)(({ theme }) => ({
    margin: theme.spacing(1, 0, 2),
}));

const CautionTextField = styled(TextField, { shouldForwardProp: (prop) => prop !== '$caution' })(({ theme, $caution }) =>
    $caution
        ? {
              '& .MuiFormHelperText-root': { color: theme.palette.warning.main },
              '& .MuiOutlinedInput-notchedOutline, & .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.warning.main },
          }
        : {},
);

const CautionHelperText = styled(FormHelperText)(({ theme }) => ({
    color: theme.palette.warning.main,
}));

const RefField = ({ value, onChange, helperText, isUrl = false, splitsCredentials = false, ...props }) => {
    const found = (isUrl && (splitsCredentials ? environmentUrlWarning(value) : urlWarning(value))) || secretWarning(value);
    const warning = splitsCredentials && found === CREDENTIAL_LINK_WARNING ? '' : found;
    const caution = warning === CREDENTIAL_LINK_WARNING;
    return (
        <CautionTextField
            fullWidth
            size="small"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            error={Boolean(warning) && !caution}
            $caution={caution}
            helperText={warning || helperText}
            {...props}
        />
    );
};

const PasswordField = ({ value, onChange, ...props }) => {
    const [revealed, setRevealed] = useState(false);
    return (
        <TextField
            fullWidth
            size="small"
            type={revealed ? 'text' : 'password'}
            autoComplete="new-password"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            InputProps={{
                endAdornment: (
                    <InputAdornment position="end">
                        <Tooltip title={revealed ? 'Hide password' : 'Show password'}>
                            <IconButton size="small" aria-label={revealed ? 'Hide password' : 'Show password'} onClick={() => setRevealed((prev) => !prev)}>
                                {revealed ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                            </IconButton>
                        </Tooltip>
                    </InputAdornment>
                ),
            }}
            {...props}
        />
    );
};

const RichField = ({ label, initialContent, placeholder, onChange }) => {
    const [warning, setWarning] = useState(() => richSecretWarning(initialContent));
    const HelperText = warning === CREDENTIAL_LINK_WARNING ? CautionHelperText : FormHelperText;
    return (
        <>
            <FieldLabel>{label}</FieldLabel>
            <MarkdownEditor
                initialContent={initialContent}
                autoFocus={false}
                placeholder={placeholder}
                onChange={(value) => {
                    onChange(value);
                    setWarning(richSecretWarning(value));
                }}
            />
            {warning && <HelperText error={warning !== CREDENTIAL_LINK_WARNING}>{warning}</HelperText>}
        </>
    );
};

const TagContextDialog = ({ tag, onClose, onSaved }) => {
    const dispatch = useDispatch();
    const fullScreen = useMediaQuery((theme) => theme.breakpoints.down('sm'));
    const [loading, setLoading] = useState(false);
    const [loadFailed, setLoadFailed] = useState(false);
    const [attempt, setAttempt] = useState(0);
    const [form, setForm] = useState(() => formFromContext(null));
    const [initialRich, setInitialRich] = useState({});
    const [editorKey, setEditorKey] = useState(0);
    const [saving, setSaving] = useState(false);
    const richRef = useRef({});

    useEffect(() => {
        if (!tag) return undefined;
        let active = true;
        setLoading(true);
        setLoadFailed(false);
        setSaving(false);
        dispatch(
            getTagDetail(tag._id, (data) => {
                if (!active) return;
                setLoading(false);
                if (!data) {
                    setLoadFailed(true);
                    return;
                }
                const context = data.context || {};
                const rich = Object.fromEntries(RICH_FIELDS.map((field) => [field, context[field] || null]));
                richRef.current = { ...rich };
                setInitialRich(rich);
                setForm(formFromContext(context));
                setEditorKey((count) => count + 1);
            }),
        );
        return () => {
            active = false;
        };
    }, [dispatch, tag, attempt]);

    const setField = (field) => (value) => setForm((prev) => ({ ...prev, [field]: value }));

    const setEnvironment = (key, field, value) => setForm((prev) => ({ ...prev, environments: prev.environments.map((env) => (env.key === key ? { ...env, [field]: value } : env)) }));

    const splitEnvironmentUrl = (key) =>
        setForm((prev) => {
            const target = prev.environments.find((env) => env.key === key);
            const parsed = target && splitUrlCredentials(target.url);
            if (!parsed || parsed.url === target.url) return prev;
            return {
                ...prev,
                environments: prev.environments.map((env) =>
                    env.key === key ? { ...env, url: parsed.url, username: parsed.username || env.username, password: parsed.password || env.password } : env,
                ),
            };
        });

    const environmentsFull = form.environments.length >= MAX_ENVIRONMENTS;

    const addEnvironment = () =>
        setForm((prev) =>
            prev.environments.length >= MAX_ENVIRONMENTS
                ? prev
                : { ...prev, environments: [...prev.environments, withKey({ name: '', url: '', username: '', password: '', vaultRef: '', branch: '' })] },
        );

    const removeEnvironment = (key) => setForm((prev) => ({ ...prev, environments: prev.environments.filter((env) => env.key !== key) }));

    const toggleCollector = (key, enabled) =>
        setForm((prev) => {
            const collectors = { ...prev.collectors };
            if (enabled) collectors[key] = collectors[key] || {};
            else delete collectors[key];
            return { ...prev, collectors };
        });

    const setExpectedTag = (value) => setForm((prev) => ({ ...prev, collectors: { ...prev.collectors, analytics: { ...(prev.collectors.analytics || {}), expectedTag: value } } }));

    const handleSave = () => {
        setSaving(true);
        dispatch(
            updateTagContext(tag._id, buildContext(form, richRef.current), (data) => {
                setSaving(false);
                if (!data) return;
                dispatch(showAlert({ severity: 'success', message: data.message || 'Context saved.' }));
                onSaved();
            }),
        );
    };

    const urlsValid =
        [form.site, form.hostDashboardUrl, form.repositoryUrl, form.designsUrl, form.qaBoardUrl].every((value) => !urlWarning(value)) &&
        form.environments.every((env) => !environmentUrlWarning(env.url));
    const ready = !loading && !loadFailed && urlsValid && !form.environments.some(missingEnvironmentName);

    return (
        <Dialog open={Boolean(tag)} onClose={onClose} maxWidth="md" fullWidth fullScreen={fullScreen}>
            <DialogTitle>Context for “{tag?.code}”</DialogTitle>
            <DialogContent>
                {loading ? (
                    <CenteredState>
                        <CircularProgress size={28} />
                    </CenteredState>
                ) : loadFailed ? (
                    <Typography color="error">
                        Context couldn’t be loaded.{' '}
                        <Link component="button" type="button" variant="body1" onClick={() => setAttempt((count) => count + 1)}>
                            Retry
                        </Link>
                    </Typography>
                ) : (
                    <>
                        <DialogContentText>Where this tag’s work happens. Use links and vault references only, never passwords where possible.</DialogContentText>

                        <SectionTitle variant="subtitle1">For the team</SectionTitle>
                        <SectionHelper variant="body2">Shown on projects and tasks that carry this tag.</SectionHelper>

                        <FormField>
                            <RefField isUrl label="Site URL" placeholder="https://www.example.com" value={form.site} onChange={setField('site')} />
                        </FormField>
                        <Row>
                            <HalfWidthField>
                                <TextField fullWidth size="small" label="Host" placeholder="e.g. Pantheon" value={form.hostProvider} onChange={(e) => setField('hostProvider')(e.target.value)} />
                            </HalfWidthField>
                            <HalfWidthField>
                                <RefField isUrl label="Host dashboard URL" placeholder="Link to the site in the host’s dashboard" value={form.hostDashboardUrl} onChange={setField('hostDashboardUrl')} />
                            </HalfWidthField>
                        </Row>
                        <Row>
                            <HalfWidthField>
                                <RefField isUrl label="Repository URL" placeholder="https://github.com/…" value={form.repositoryUrl} onChange={setField('repositoryUrl')} />
                            </HalfWidthField>
                            <HalfWidthField>
                                <RefField isUrl label="Designs URL" placeholder="Figma or drive folder link" value={form.designsUrl} onChange={setField('designsUrl')} />
                            </HalfWidthField>
                        </Row>
                        <Row>
                            <HalfWidthField>
                                <Autocomplete
                                    freeSolo
                                    options={QA_BOARD_TYPES}
                                    inputValue={form.qaBoardType}
                                    onInputChange={(event, next) => setField('qaBoardType')(next)}
                                    renderInput={(params) => <TextField {...params} size="small" label="QA board" placeholder="e.g. BugHerd" />}
                                />
                            </HalfWidthField>
                            <HalfWidthField>
                                <RefField isUrl label="QA board URL" placeholder="Link to the external QA board" value={form.qaBoardUrl} onChange={setField('qaBoardUrl')} />
                            </HalfWidthField>
                        </Row>

                        <FormField>
                            <FieldLabel>Environments</FieldLabel>
                            {form.environments.map((env) => (
                                <EnvironmentRow key={env.key}>
                                    <EnvironmentCell $area="name">
                                        <Autocomplete
                                            freeSolo
                                            options={ENVIRONMENT_NAMES}
                                            inputValue={env.name}
                                            onInputChange={(event, next) => setEnvironment(env.key, 'name', next)}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    size="small"
                                                    label="Name"
                                                    placeholder="e.g. Live"
                                                    error={missingEnvironmentName(env)}
                                                    helperText={missingEnvironmentName(env) ? 'Name this environment' : undefined}
                                                />
                                            )}
                                        />
                                    </EnvironmentCell>
                                    <EnvironmentCell $area="url">
                                        <RefField
                                            isUrl
                                            splitsCredentials
                                            label="URL"
                                            placeholder="Site or admin URL"
                                            value={env.url}
                                            onChange={(value) => setEnvironment(env.key, 'url', value)}
                                            onBlur={() => splitEnvironmentUrl(env.key)}
                                        />
                                    </EnvironmentCell>
                                    <EnvironmentCell $area="branch">
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label="Branch"
                                            placeholder="e.g. main"
                                            value={env.branch}
                                            onChange={(e) => setEnvironment(env.key, 'branch', e.target.value)}
                                        />
                                    </EnvironmentCell>
                                    <EnvironmentCell $area="username">
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label="Username"
                                            placeholder="Basic Auth user"
                                            autoComplete="off"
                                            value={env.username}
                                            onChange={(e) => setEnvironment(env.key, 'username', e.target.value)}
                                        />
                                    </EnvironmentCell>
                                    <EnvironmentCell $area="password">
                                        <PasswordField
                                            label="Password"
                                            placeholder="Basic Auth password"
                                            value={env.password}
                                            onChange={(value) => setEnvironment(env.key, 'password', value)}
                                        />
                                    </EnvironmentCell>
                                    <EnvironmentCell $area="vault">
                                        <RefField
                                            label="Vault reference"
                                            placeholder="Link to the vault item"
                                            value={env.vaultRef}
                                            onChange={(value) => setEnvironment(env.key, 'vaultRef', value)}
                                        />
                                    </EnvironmentCell>
                                    <Tooltip title="Remove environment">
                                        <RemoveEnvButton size="small" onClick={() => removeEnvironment(env.key)}>
                                            <RemoveIcon fontSize="small" />
                                        </RemoveEnvButton>
                                    </Tooltip>
                                </EnvironmentRow>
                            ))}
                            <Tooltip title={environmentsFull ? `A tag can have at most ${MAX_ENVIRONMENTS} environments.` : ''}>
                                <TooltipAnchor>
                                    <Button size="small" startIcon={<AddIcon />} onClick={addEnvironment} disabled={environmentsFull}>
                                        Add environment
                                    </Button>
                                </TooltipAnchor>
                            </Tooltip>
                        </FormField>

                        <FormField>
                            <RichField
                                label="Test instructions"
                                key={`test-${editorKey}`}
                                initialContent={initialRich.testInstructions}
                                placeholder="How to test work on this site"
                                onChange={(value) => {
                                    richRef.current.testInstructions = value;
                                }}
                            />
                        </FormField>
                        <FormField>
                            <RichField
                                label="Notes"
                                key={`notes-${editorKey}`}
                                initialContent={initialRich.notes}
                                placeholder="Anything else the team should know"
                                onChange={(value) => {
                                    richRef.current.notes = value;
                                }}
                            />
                        </FormField>

                        <ServiceSection>
                            <SectionTitle variant="subtitle1">For automated agents</SectionTitle>
                            <SectionHelper variant="body2">Only agents and this page see these fields.</SectionHelper>
                            <Row>
                                <HalfWidthField>
                                    <TextField select fullWidth size="small" label="Site type" value={form.type} onChange={(e) => setField('type')(e.target.value)}>
                                        <MenuItem value="">
                                            <em>Not set</em>
                                        </MenuItem>
                                        {SITE_TYPES.map((option) => (
                                            <MenuItem key={option.value} value={option.value}>
                                                {option.label}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                </HalfWidthField>
                                <HalfWidthField>
                                    <RefField label="Host site ID" placeholder="The site’s ID at the host" value={form.hostSiteId} onChange={setField('hostSiteId')} />
                                </HalfWidthField>
                            </Row>
                            <FormField>
                                <RefField label="Credential reference" placeholder="Link to the vault item" value={form.credentialRef} onChange={setField('credentialRef')} />
                            </FormField>
                            <Row>
                                <HalfWidthField>
                                    <RefField label="Admin reference" placeholder="Link to the vault item" value={form.adminRef} onChange={setField('adminRef')} />
                                </HalfWidthField>
                                <HalfWidthField>
                                    <RefField label="SSH reference" placeholder="Link to the vault item" value={form.sshRef} onChange={setField('sshRef')} />
                                </HalfWidthField>
                            </Row>

                            <FieldLabel>Checks</FieldLabel>
                            <CollectorGrid>
                                {COLLECTORS.map((collector) => (
                                    <FormControlLabel
                                        key={collector.key}
                                        control={<Switch size="small" checked={Boolean(form.collectors[collector.key])} onChange={(e) => toggleCollector(collector.key, e.target.checked)} />}
                                        label={collector.label}
                                    />
                                ))}
                            </CollectorGrid>
                            {form.collectors.analytics && (
                                <FormField>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Expected analytics tag"
                                        placeholder="e.g. G-XXXXXXXXXX"
                                        value={form.collectors.analytics.expectedTag || ''}
                                        onChange={(e) => setExpectedTag(e.target.value)}
                                    />
                                </FormField>
                            )}
                            <TightDivider />

                            <RichField
                                label="Agent instructions"
                                key={`agent-${editorKey}`}
                                initialContent={initialRich.agentInstructions}
                                placeholder="Instructions for automated agents working on this site"
                                onChange={(value) => {
                                    richRef.current.agentInstructions = value;
                                }}
                            />
                        </ServiceSection>
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button variant="contained" onClick={handleSave} disabled={!ready || saving}>
                    {saving ? 'Saving…' : 'Save context'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

const Tags = () => {
    const dispatch = useDispatch();
    const [tags, setTags] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [loading, setLoading] = useState(true);
    const [loadFailed, setLoadFailed] = useState(false);
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const [clientFilter, setClientFilter] = useState(ALL_CLIENTS);
    const [showDeleted, setShowDeleted] = useState(false);
    const [clients, setClients] = useState([]);
    const [formOpen, setFormOpen] = useState(false);
    const [editingTag, setEditingTag] = useState(null);
    const [usageTag, setUsageTag] = useState(null);
    const [contextTag, setContextTag] = useState(null);
    const [confirmTarget, setConfirmTarget] = useState(null);
    const loadRequestRef = useRef(0);

    useEffect(() => {
        dispatch(
            getClients(
                0,
                0,
                (data) => {
                    const list = (Array.isArray(data) ? data : data?.clients || []).filter((client) => !client.isDeleted);
                    setClients([...list.filter((client) => client.active !== false), ...list.filter((client) => client.active === false)]);
                },
            ),
        );
    }, [dispatch]);

    useEffect(() => {
        const timer = setTimeout(() => {
            setSearch(searchInput.trim());
            setPage(0);
        }, SEARCH_DELAY_MS);
        return () => clearTimeout(timer);
    }, [searchInput]);

    const loadTags = useCallback(() => {
        const requestId = ++loadRequestRef.current;
        setLoading(true);
        const params = { start: page * PAGE_SIZE, limit: PAGE_SIZE };
        if (search) params.search = search;
        if (clientFilter._id) params.client = clientFilter._id;
        if (showDeleted) params.includeDeleted = true;
        dispatch(
            getTags(params, (data) => {
                if (requestId !== loadRequestRef.current) return;
                const lastPage = Math.max(0, Math.ceil((data?.total || 0) / PAGE_SIZE) - 1);
                if (data && !data.tags?.length && page > lastPage) {
                    setPage(lastPage);
                    return;
                }
                setLoading(false);
                setLoadFailed(!data);
                setTags(data?.tags || []);
                setTotal(data?.total || 0);
            }),
        );
    }, [dispatch, page, search, clientFilter, showDeleted]);

    useEffect(() => {
        loadTags();
    }, [loadTags]);

    const handleCreateClick = () => {
        setEditingTag(null);
        setFormOpen(true);
    };

    const handleEditClick = (tag) => {
        setEditingTag(tag);
        setFormOpen(true);
    };

    const handleSaved = () => {
        setFormOpen(false);
        loadTags();
    };

    const handleConfirm = () => {
        const tag = confirmTarget;
        setConfirmTarget(null);
        const action = tag.isDeleted ? restoreTag : deleteTag;
        dispatch(
            action(tag._id, (data) => {
                if (!data) return;
                dispatch(showAlert({ severity: 'success', message: data.message || 'Tag updated.' }));
                loadTags();
            }),
        );
    };

    const hasFilters = Boolean(search || clientFilter._id || showDeleted);

    return (
        <AdminPageContainer>
            <PageHeader
                title="Tags"
                subtitle="Create and manage the tags used on clients, accounts, groups, projects and tasks"
                actions={
                    <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={handleCreateClick}>
                        Create tag
                    </Button>
                }
            />

            <FilterRow>
                <SearchField
                    placeholder="Search by code or label…"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon />
                            </InputAdornment>
                        ),
                    }}
                />
                <ClientFilter
                    disableClearable
                    options={[ALL_CLIENTS, GENERAL, ...clients]}
                    value={clientFilter}
                    onChange={(event, next) => {
                        setClientFilter(next || ALL_CLIENTS);
                        setPage(0);
                    }}
                    getOptionLabel={clientOptionLabel}
                    isOptionEqualToValue={(option, value) => option._id === value._id}
                    renderInput={(params) => <TextField {...params} label="Client" />}
                />
                <FormControlLabel
                    control={
                        <Switch
                            checked={showDeleted}
                            onChange={(e) => {
                                setShowDeleted(e.target.checked);
                                setPage(0);
                            }}
                        />
                    }
                    label="Show deleted"
                />
            </FilterRow>

            {loading && tags.length === 0 ? (
                <CenteredState>
                    <CircularProgress />
                </CenteredState>
            ) : loadFailed ? (
                <CenteredState>
                    <Typography color="text.secondary">Tags couldn’t be loaded. Try again.</Typography>
                </CenteredState>
            ) : tags.length === 0 ? (
                <CenteredState>
                    <Typography color="text.secondary">{hasFilters ? 'No tags match these filters.' : 'No tags yet. Use “Create tag” to add the first one.'}</Typography>
                </CenteredState>
            ) : (
                <ScrollContainer component={Paper}>
                    <TagTable size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Code</TableCell>
                                <WideOnlyCell>Label</WideOnlyCell>
                                <TableCell>Client</TableCell>
                                <WideOnlyCell>Created</WideOnlyCell>
                                <TableCell align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {tags.map((tag) => (
                                <TagRow key={tag._id} hover muted={tag.isDeleted}>
                                    <TableCell>
                                        <CodeCell>
                                            <CodeText variant="body2">{tag.code}</CodeText>
                                            {tag.hasContext && (
                                                <Tooltip title="This tag has context for developers and agents">
                                                    <Chip size="small" color="primary" variant="outlined" icon={<ContextIcon />} label="Context" />
                                                </Tooltip>
                                            )}
                                            {tag.isDeleted && <Chip size="small" color="error" variant="outlined" label="Deleted" />}
                                        </CodeCell>
                                        {tag.label && <NarrowLabel variant="caption">{tag.label}</NarrowLabel>}
                                    </TableCell>
                                    <WideOnlyCell>{tag.label || '—'}</WideOnlyCell>
                                    <TableCell>{tag.client ? tag.client.name : <Chip size="small" variant="outlined" label="General" />}</TableCell>
                                    <WideOnlyCell>{formatDateTime(tag.createdAt)}</WideOnlyCell>
                                    <NoWrapCell align="right">
                                        <Tooltip title="Where used">
                                            <IconButton size="small" onClick={() => setUsageTag(tag)}>
                                                <UsageIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        {!tag.isDeleted && (
                                            <Tooltip title="Edit context">
                                                <IconButton size="small" color="primary" onClick={() => setContextTag(tag)}>
                                                    <ContextIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                        {!tag.isDeleted && (
                                            <Tooltip title="Edit tag">
                                                <IconButton size="small" color="primary" onClick={() => handleEditClick(tag)}>
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                        {tag.isDeleted ? (
                                            <Tooltip title="Restore tag">
                                                <IconButton size="small" color="primary" onClick={() => setConfirmTarget(tag)}>
                                                    <RestoreIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        ) : (
                                            <Tooltip title="Delete tag">
                                                <IconButton size="small" color="error" onClick={() => setConfirmTarget(tag)}>
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                    </NoWrapCell>
                                </TagRow>
                            ))}
                        </TableBody>
                    </TagTable>
                    <TablePagination component="div" count={total} page={page} rowsPerPage={PAGE_SIZE} rowsPerPageOptions={[PAGE_SIZE]} onPageChange={(event, next) => setPage(next)} />
                </ScrollContainer>
            )}

            <TagFormDialog open={formOpen} tag={editingTag} clients={clients} onClose={() => setFormOpen(false)} onSaved={handleSaved} />

            <TagUsageDialog tag={usageTag} onClose={() => setUsageTag(null)} />

            <TagContextDialog
                tag={contextTag}
                onClose={() => setContextTag(null)}
                onSaved={() => {
                    setContextTag(null);
                    loadTags();
                }}
            />

            <ConfirmDialog
                open={Boolean(confirmTarget)}
                onClose={() => setConfirmTarget(null)}
                onConfirm={handleConfirm}
                title={confirmTarget?.isDeleted ? 'Restore tag' : 'Delete tag'}
                message={
                    confirmTarget?.isDeleted
                        ? `Restore “${confirmTarget?.code}”? It comes back everywhere it was assigned.`
                        : `Delete “${confirmTarget?.code}”? It’s hidden everywhere it’s assigned, and can be restored later.`
                }
                confirmLabel={confirmTarget?.isDeleted ? 'Restore tag' : 'Delete tag'}
                confirmColor={confirmTarget?.isDeleted ? 'primary' : 'error'}
            />
        </AdminPageContainer>
    );
};

export default Tags;
